import React, { useState, useRef, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  apiFormDataRequest,
  apiGetRequest,
  apiPostRequest,
} from "helpers/Requests";
import { useForm } from "react-hook-form";
import {
  baseURLImage,
  classNames,
  downloadFileFromServer,
  extractFileNameFromS3URL,
  getExtension,
  getFileExtension,
  getNameProfile,
  getPaginationObj,
  getUrlRole,
} from "Utils/utils";
import { openAttachmentPreviewInNewTab } from "Utils/attachmentPreview";
import { toast } from "react-toastify";
import { USER_ROLE, emptyPaginationObj } from "Constants/constant";
import { useSelector } from "react-redux";
import { getUser } from "store/selector/auth.selector";
import { roleAccess } from "helpers/RolesPermission";
import {
  BLK_PORTAL_PREMISSION_KEYS_CHILDREN,
  PERMISSION_KEYS,
} from "store/constants";
import { LoginContext } from "helpers/LoginContext";
import {
  HiDownload,
  HiEye,
  HiOutlinePencil,
  HiOutlineTrash,
  HiCheck,
  HiX,
} from "react-icons/hi";
import SwitchInput from "components/common/Input/SwitchInput";
import { BiPlus } from "react-icons/bi";
import MultiFileInput from "components/common/FileUpload/MultiFileInput";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import CustomButton from "components/common/Button/CustomButton";
import LoaderButton from "components/common/LoaderButton/LoaderButton";
import DeleteCaseNoteModal from "./Modals/DeleteCaseNoteModal";
import EditCaseNoteModal from "./Modals/EditCaseNoteModal";
import FileIcon from "components/common/FileIcon/FileIcon";
import * as FileSaver from "file-saver";
import {
  BASE_URL,
  URL_SIL_ACCOMMODATION_CASE_NOTE_CREATE,
  URL_SIL_ACCOMMODATION_CASE_NOTES_LIST,
} from "store/services/URL";
import { uploadFilesOnVapour } from "Utils/uploadFileOnS3";
import { formatDistanceToNow } from "date-fns";
import {
  getClientLocalDateString,
  toCaseNoteDateInputValue,
} from "./caseNoteLocalDate";

const getEmptyCaseNoteFormValues = () => ({
  comment: "",
  title: "",
  duration: "",
  contact_method: "",
  distance_km: "",
  non_direct_support: "",
  is_billable: false,
  attachments: [],
  show_to_worker: false,
  worker_to_worker: false,
  note_date: getClientLocalDateString(),
});

/**
 * Calendar day key YYYY-MM-DD for ordering (matches DB COALESCE(note_date, …)).
 * Uses string compare so `YYYY-MM-DD` from the API is not shifted by local timezone.
 */
const toDisplayedDayKey = (note) => {
  const unwrap = (v) => {
    if (v == null || v === "") return null;
    if (typeof v === "object" && v !== null && "date" in v) {
      return unwrap(v.date);
    }
    const s = String(v).trim();
    if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) {
      return s.slice(0, 10);
    }
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      return d.toISOString().slice(0, 10);
    }
    return null;
  };

  return (
    unwrap(note?.note_date) ||
    unwrap(note?.date) ||
    unwrap(note?.created_at) ||
    "1970-01-01"
  );
};

/** Newest displayed date first; same calendar day by created_at (newest first). */
const sortCaseNotesByDisplayedDateDesc = (notes) =>
  [...notes].sort((a, b) => {
    const dayCmp = toDisplayedDayKey(b).localeCompare(toDisplayedDayKey(a));
    if (dayCmp !== 0) return dayCmp;
    return (
      new Date(b.created_at || 0).getTime() -
      new Date(a.created_at || 0).getTime()
    );
  });

const CaseNotes = (props) => {
  const [caseNoteFormResetKey, setCaseNoteFormResetKey] = useState(0);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
  } = useForm({ defaultValues: getEmptyCaseNoteFormValues() });

  const watchFormData = watch();

  const { participantInfo, isCaseNoteCall, callFrom = null } = props;

  const { uuid } = useParams();
  const currentUser = useSelector(getUser);
  const { isUserGracePeriodOver } = useContext(LoginContext);

  const myRef = useRef(null);
  const [paginationObj, setPaginationObj] = useState(emptyPaginationObj);
  const [reachedLast, setReachedLast] = useState(false);

  const [selectedComment, setSelectedComment] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [fetchNotes, setFetchNotes] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [fileData, setFilesData] = useState([]);

  const [request, setRequest] = useState({
    isRequested: false,
    isSuccess: false,
    isFailed: false,
  });

  const skipWorkerToWorkerSyncRef = useRef(true);

  useEffect(() => {
    scrollToRef(myRef);
  }, [myRef]);

  useEffect(() => {
    fetchCaseNotes({ page: 1 });
  }, []);

  useEffect(() => {
    if (!reachedLast) return;
    if (paginationObj.totalPages <= paginationObj.page) {
      setReachedLast(false);
      return;
    }
    const nextPage = paginationObj.page + 1;
    setPaginationObj((p) => ({ ...p, page: nextPage }));
    fetchCaseNotes({ page: nextPage });
    setReachedLast(false);
  }, [reachedLast]);

  useEffect(() => {
    if (skipWorkerToWorkerSyncRef.current) {
      skipWorkerToWorkerSyncRef.current = false;
      return;
    }
    setPaginationObj({ ...emptyPaginationObj });
    setFetchNotes([]);
    setReachedLast(false);
    fetchCaseNotes({ page: 1 });
  }, [watchFormData?.worker_to_worker]);

  const scrollToRef = (ref) => {
    if (ref?.current) {
      ref.current.scrollTop = 0;
    }
  };

  useEffect(() => {
    if (isCaseNoteCall) handleFocusOnCaseNote();
  }, [isCaseNoteCall]);

  const handleFocusOnCaseNote = () => {
    if (document.getElementById("caseNote")) {
      document.getElementById("caseNote").focus();
    }
  };

  const [contactMethods, setContactMethods] = useState([]);

  useEffect(() => {
    async function fetchContactMethods() {
      try {
        const res = await apiGetRequest("cos/contact-types-list?status=all");
        // Adjust the path to your API response structure
        setContactMethods(res.data.contact_type_list || []);
      } catch (err) {
        setContactMethods([]);
      }
    }
    fetchContactMethods();
  }, []);
  const fetchCaseNotes = async (opts = {}) => {
    const page = opts.page ?? paginationObj.page;
    if (!reachedLast) setRequest({ ...request, isRequested: true });
    try {
      let res =
        callFrom === "silProfile"
          ? await apiGetRequest(
              `/${URL_SIL_ACCOMMODATION_CASE_NOTES_LIST.replace(
                BASE_URL,
                ""
              )}/${participantInfo?.uuid}?page=${page}&worker_to_worker=${
                watchFormData?.worker_to_worker ? "true" : "false"
              }`
            )
          : await apiGetRequest(
              `/${getUrlRole()}/case-notes?pt_id=${uuid}&page=${page}&worker_to_worker=${
                watchFormData?.worker_to_worker ? "true" : "false"
              }`
            );
      if (res.status !== 200) {
        setRequest({ ...request, isRequested: false, isFailed: true });
      }
      if (res.status === 200) {
        setRequest({ ...request, isRequested: false, isSuccess: true });

        if (callFrom === "silProfile" && res?.data?.data?.notes != null) {
          const payload = res.data.data.notes;
          const incoming = payload.data || [];
          const curPage = payload.current_page ?? 1;
          setFetchNotes((prev) => {
            const merged =
              curPage <= 1
                ? [...incoming]
                : [
                    ...prev,
                    ...incoming.filter(
                      (n) => !prev.some((p) => p.id === n.id)
                    ),
                  ];
            return sortCaseNotesByDisplayedDateDesc(merged);
          });
          setPaginationObj(getPaginationObj(payload));
        } else if (res?.data?.case_notes != null) {
          const payload = res.data.case_notes;
          const incoming = payload.data || [];
          const curPage = payload.current_page ?? 1;
          setFetchNotes((prev) => {
            const merged =
              curPage <= 1
                ? [...incoming]
                : [
                    ...prev,
                    ...incoming.filter(
                      (n) => !prev.some((p) => p.id === n.id)
                    ),
                  ];
            return sortCaseNotesByDisplayedDateDesc(merged);
          });
          setPaginationObj(getPaginationObj(payload));
        }
        setReachedLast(false);
      }
    } catch (err) {
      setRequest({ ...request, isRequested: false, isFailed: true });
    }
  };

  const onSubmit = async (data) => {
    if (isSubmitting || isUserGracePeriodOver) return;
    setIsSubmitting(true);
    let uploadedFiles = [];

    if (callFrom === "silProfile") {
      uploadedFiles = await uploadFilesOnVapour(data?.attachments);
    }
    try {
      const preparFilesData =
        Array.isArray(fileData) &&
        fileData?.length > 0 &&
        data?.attachments?.length > 0 &&
        fileData?.length === data?.attachments?.length &&
        fileData?.map((item, index) => ({
          key: item?.key,
          ext: getExtension(data?.attachments?.[index]?.type),
          name: data?.attachments?.[index]?.name,
        }));
      const formData = new FormData();
      const payloadData = {};
      if (callFrom === "silProfile") {
        Object.assign(payloadData, {
          home_uuid: participantInfo.uuid,
          user_id: currentUser.id,
          notes: data.comment,
          attachments: uploadedFiles,
          show_to_worker: watchFormData?.show_to_worker,
        });
      } else {
        formData.append("pt_id", participantInfo.id);
        formData.append("notes", data.comment);
        if (preparFilesData && preparFilesData?.length > 0) {
          preparFilesData?.forEach((file, index) => {
            formData.append(`attachments[${index}][key]`, file.key);
            formData.append(`attachments[${index}][ext]`, file.ext);
            formData.append(`attachments[${index}][name]`, file.name);
          });
        }
        if (roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])) {
          formData.append("show_to_worker", watchFormData?.show_to_worker);
        }
        formData.append("is_billable", data.is_billable || false);
        formData.append("title", data.title || "");
        formData.append("duration", data.duration || "");
        formData.append("contact_method", data.contact_method || "");
        formData.append("distance_km", data.distance_km || "");
        formData.append(
          "non_direct_support",
          data.non_direct_support ? "1" : "0"
        );
        formData.append(
          "note_date",
          data.note_date || getClientLocalDateString()
        );
      }

      if (
        roleAccess([USER_ROLE.Sp_Standard_User]) &&
        callFrom !== "silProfile"
      ) {
        formData.append("worker_to_worker", watchFormData?.worker_to_worker);
      }

      const response =
        callFrom === "silProfile"
          ? await apiPostRequest(
              URL_SIL_ACCOMMODATION_CASE_NOTE_CREATE.replace(BASE_URL, ""),
              payloadData
            )
          : await apiFormDataRequest(`/${getUrlRole()}/case-notes`, formData);

      const createdCaseNote =
        callFrom === "silProfile"
          ? response.data.note
          : response.data.case_notes;

      const normalizedCaseNote =
        callFrom === "silProfile"
          ? createdCaseNote
          : {
              ...createdCaseNote,
              note_date: toCaseNoteDateInputValue(
                createdCaseNote?.note_date ?? data.note_date
              ),
            };

      setFetchNotes(
        sortCaseNotesByDisplayedDateDesc([...fetchNotes, normalizedCaseNote])
      );
      reset(getEmptyCaseNoteFormValues());
      setCaseNoteFormResetKey((k) => k + 1);
      setFilesData([]);
      setIsSubmitting(false);
      toast.success(
        `${callFrom === "silProfile" ? "Note" : "Case note"} added successfully`
      );
    } catch (err) {
      toast.error(err?.message || "Something went wrong!");
      setIsSubmitting(false);
    }
  };

  const handleImageError = (e, fname, lname) => {
    return (e.target.src = getNameProfile(fname, lname));
  };

  const handleScroll = () => {
    if (myRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = myRef.current;
      if (
        scrollTop + clientHeight + 10 >= scrollHeight &&
        paginationObj.totalPages > paginationObj.page
      ) {
        setReachedLast(true);
      }
    }
  };

  const handleDownload = (file, name) => {
 
    if (file instanceof File) {

      const blob = new Blob([file], { type: file.type });
      FileSaver.saveAs(blob, name || "downloadedFile");
    } else {

      downloadFileFromServer(
        file,
        name ? name : `download.${getFileExtension(file)}`
      );
    }
  };

  const renderItem = (items, indexKey) => {
    return (
      <div className='w-full min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[min(320px,50vh)] overflow-y-auto overflow-x-hidden custom-scroll'>
        {items?.map((item, index) => {
          const fileName =
            callFrom === "silProfile" ? item?.name : item?.original_name;

          const file =
            callFrom === "silProfile" ? item?.attachment : item?.attachments;

          const actualName =
            (fileName && String(fileName)) ||
            extractFileNameFromS3URL(file) ||
            "";
          const displayName = `download (${index + 1})`;
          const tooltipTitle = actualName || String(file || "").slice(0, 200);

          const rowKey =
            item?.id != null ? `att-${item.id}` : `${indexKey}-att-${index}`;

          return (
            <div
              key={rowKey}
              className='min-w-0 max-w-full flex items-center gap-2 border border-gray-200 rounded-md px-2 py-1.5 text-sm bg-white'
            >
              <div className='flex-shrink-0 bg-blue-50 rounded-full h-8 w-8 flex items-center justify-center'>
                {FileIcon(
                  getFileExtension(
                    fileName
                      ? fileName.toLowerCase()
                      : extractFileNameFromS3URL(file)?.toLowerCase()
                  ),
                  12
                )}
              </div>
              <span
                className='min-w-0 flex-1 font-medium text-gray-900 truncate text-left'
                title={tooltipTitle || displayName}
              >
                {displayName}
              </span>
              <div className='flex flex-shrink-0 items-center gap-1'>
                <button
                  type='button'
                  onClick={() =>
                    openAttachmentPreviewInNewTab(file, fileName)
                  }
                  className='icon-eye-wrapper cursor-pointer'
                  title={tooltipTitle ? `View — ${tooltipTitle}` : "View"}
                >
                  <HiEye className='icon-size' />
                </button>
                <button
                  type='button'
                  onClick={() => handleDownload(file, fileName)}
                  className='icon-download-wrapper cursor-pointer'
                  title={
                    tooltipTitle
                      ? `Download — ${tooltipTitle}`
                      : "Download"
                  }
                >
                  <HiDownload className='icon-size' />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th"; // covers 11th, 12th, 13th, etc.
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const abbreviateMonthIfNeeded = (month) => {
    const longMonths = ["September", "October", "November", "December"];
    if (longMonths.includes(month)) {
      return month.slice(0, 3); // Abbreviate to Sep, Oct, etc.
    }
    return month; // Keep full month name for other months
  };

  const formatDateWithSuffix = (date) => {
    const d = new Date(date);
    const day = d.getDate();
    const month = d.toLocaleString("en-AU", { month: "long" });
    const year = d.getFullYear();
    const dayWithSuffix = `${day}${getOrdinalSuffix(day)}`;
    const formattedMonth = abbreviateMonthIfNeeded(month);

    return `${dayWithSuffix} ${formattedMonth} ${year}`;
  };

  /** API sends UTC; naive strings (no Z / offset) are parsed as UTC, then shown in client local time. */
  const dateFromUtcCreatedAt = (value) => {
    if (value == null || value === "") return null;
    const s = String(value).trim();
    const hasExplicitTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
    const isoLike = s.includes("T") ? s : s.replace(" ", "T");
    const ms = new Date(
      hasExplicitTz ? s : /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(isoLike) ? `${isoLike}Z` : s
    ).getTime();
    return Number.isNaN(ms) ? null : new Date(ms);
  };

  const formatRelativeCreatedAt = (value) => {
    const d = dateFromUtcCreatedAt(value);
    if (!d) return "";
    return formatDistanceToNow(d, { addSuffix: true });
  };

  const formatCreatedAtTooltip = (value) => {
    const d = dateFromUtcCreatedAt(value);
    if (!d) return undefined;
    return `${formatDateWithSuffix(d)} ${d.toLocaleTimeString("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };

  const formatDateTimeEnAU = (value) => {
    const d = dateFromUtcCreatedAt(value);
    if (!d) return "";
    return `${formatDateWithSuffix(d)} ${d.toLocaleTimeString("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };

  return (
    <>
      <section aria-labelledby='notes-title'>
        <div className='bg-white border border-gray-200 shadow-lg rounded-xl overflow-hidden'>
          <div>
            {(roleAccess(
              [USER_ROLE.Sp_Standard_User],
              PERMISSION_KEYS.PARTICIPANT,
              BLK_PORTAL_PREMISSION_KEYS_CHILDREN.PARTICIPANT.CASE_NOTES.STORE
            ) ||
              roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])) && (
              <div className='bg-white border-b border-gray-200 px-4 sm:px-6 py-4 sm:py-5 mb-6'>
                <div className='mb-4'>
                  <h3 className='text-lg font-semibold text-gray-900'>Add Case Note</h3>
                </div>
                <div className='flex space-x-3 sm:space-x-4 items-start'>
                  {currentUser && (
                    <div className='flex-shrink-0'>
                      <img
                        onError={(e) =>
                          handleImageError(e, currentUser.name || " ", " ")
                        }
                        className='h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover ring-2 ring-white shadow-md'
                        src={
                          currentUser.profile_picture
                            ? baseURLImage(currentUser.profile_picture)
                            : getNameProfile(currentUser.name || " ", " ")
                        }
                        alt='profilePicture'
                      />
                    </div>
                  )}
                  <div className='flex-1 min-w-0'>
                    <form onSubmit={handleSubmit(onSubmit)}>
                      {callFrom !== "silProfile" && (
                        <div className='mb-3'>
                          <input
                            type='text'
                            name='title'
                            id='caseNote'
                            className='block w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all'
                            placeholder='Subject Line'
                            {...register("title")}
                            maxLength={150}
                          />
                        </div>
                      )}
                      <div className='mb-3'>
                        {callFrom === "silProfile" ? (
                          <textarea
                            id='caseNote'
                            name='comment'
                            rows={3}
                            className='block w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all resize-none'
                            placeholder='Write a note...'
                            {...register("comment", { required: true })}
                          />
                        ) : (
                          <div className='border border-gray-300 rounded-lg overflow-hidden shadow-sm'>
                            <CKEditor
                              key={caseNoteFormResetKey}
                              editor={ClassicEditor}
                              data={watchFormData?.comment || ""}
                              config={{ placeholder: "Enter case note here..." }}
                              onChange={(_, editor) =>
                                setValue("comment", editor.getData(), {
                                  shouldValidate: true,
                                })
                              }
                            />
                          </div>
                        )}
                      </div>
                      {errors.comment && (
                        <span className='text-red-600 text-xs'>
                          {(callFrom === "silProfile" ? "Note" : "Case note") +
                            " is required"}
                        </span>
                      )}
                      {callFrom !== "silProfile" && (
                        <div className='mb-3'>
                          <label className='block text-sm font-medium text-gray-700 mb-1'>
                            Date
                          </label>
                          <input
                            type='date'
                            name='note_date'
                            className='block w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all'
                            {...register("note_date")}
                            max={getClientLocalDateString()}
                          />
                        </div>
                      )}
                      <div className='mb-2'>
                        <MultiFileInput
                          key={caseNoteFormResetKey}
                          filenameLimit={150}
                          type='casenote'
                          handleFile={(e) => setValue("attachments", e)}
                          size={5}
                          files={watchFormData?.attachments}
                          handleDelete={(e) => setValue("attachments", e)}
                          showDragAndDrop={false}
                          isHardClearAllStates={isSubmitting === true}
                          setFilesData={setFilesData}
                          setIsSubmitting={setIsSubmitting}
                          isFileUploadToS3={callFrom !== "silProfile"}
                        />
                      </div>
                      {callFrom !== "silProfile" && (
                        <div className='grid grid-cols-2 gap-3 mb-3'>
                          <input
                            type='text'
                            name='duration'
                            placeholder='Time Spent (00:00)'
                            pattern='^([0-9]{1,2}):([0-5][0-9])$'
                            maxLength='5'
                            title='Enter duration in HH:MM format (e.g., 02:30)'
                            className='block w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all'
                            {...register("duration", {
                              pattern: {
                                value: /^([0-9]{1,2}):([0-5][0-9])$/,
                                message:
                                  "Please enter duration in HH:MM format",
                              },
                              validate: (value) => {
                                if (!value) return true;
                                const [hours, minutes] = value
                                  .split(":")
                                  .map(Number);
                                return (
                                  (hours >= 0 &&
                                    hours <= 99 &&
                                    minutes >= 0 &&
                                    minutes <= 59) ||
                                  "Invalid duration format"
                                );
                              },
                              onChange: (e) => {
                                let value = e.target.value.replace(/\D/g, "");
                                if (value.length >= 3) {
                                  value =
                                    value.slice(0, 2) + ":" + value.slice(2, 4);
                                }
                                e.target.value = value;
                              },
                            })}
                          />
                          <select
                            className='block w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all bg-white'
                            {...register("contact_method")}
                          >
                            <option value=''>How Contacted</option>
                            {contactMethods.map((method) => (
                              <option key={method.title} value={method.title}>
                                {method.title}
                              </option>
                            ))}
                          </select>
                          <input
                            type='number'
                            name='distance_km'
                            placeholder='Travel (KM)'
                            step='0.01'
                            min='0'
                            className='block w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all'
                            {...register("distance_km", {
                              pattern: {
                                value: /^\d*\.?\d*$/,
                                message: "Please enter a valid number",
                              },
                            })}
                          />
                          <select
                            className='block w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all bg-white'
                            {...register("non_direct_support")}
                          >
                            <option value=''>Non-direct support</option>
                            <option value='true'>Yes</option>
                            <option value='false'>No</option>
                          </select>
                        </div>
                      )}
                      {callFrom !== "silProfile" && (
                        <div className='flex flex-wrap gap-x-4 gap-y-2 items-center mb-2'>
                          <SwitchInput
                            handleChange={(e) => setValue("is_billable", e)}
                            checked={watchFormData?.is_billable || false}
                            label='Billable'
                          />

                          {roleAccess([
                            USER_ROLE.Sp_Admin,
                            USER_ROLE.Sp_manager,
                          ]) && (
                            <SwitchInput
                              handleChange={(e) =>
                                setValue("show_to_worker", e)
                              }
                              checked={watchFormData?.show_to_worker || false}
                              label='Show to worker'
                            />
                          )}
                          {roleAccess([USER_ROLE.Sp_Standard_User]) && (
                            <SwitchInput
                              label='Show to other workers'
                              checked={watchFormData?.worker_to_worker || false}
                              handleChange={(val) =>
                                setValue("worker_to_worker", val)
                              }
                            />
                          )}
                        </div>
                      )}
                      <div className='flex justify-end'>
                        <CustomButton
                          type='submit'
                          label='Submit'
                          showLoading={isSubmitting}
                          isDisabled={isUserGracePeriodOver || isSubmitting}
                        />
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
            <div
              onScroll={handleScroll}
              ref={myRef}
              className='px-4 sm:px-6 py-6 overflow-y-auto custom-scroll caseNotesContainer max-h-[600px]'
            >
              {request.isRequested ? (
                <div
                  style={{
                    justifyContent: "center",
                    minHeight: "100%",
                    height: "100%",
                  }}
                  className='flex items-center'
                >
                  <CircularLoader
                    classes='flex justify-center items-center'
                    classloader='loader-l'
                  />
                </div>
              ) : (
                <ul className='space-y-4'>
                  {fetchNotes &&
                    fetchNotes?.map((comment, index) => (
                      <li key={index}>
                        <div className='bg-white border border-gray-200 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-200'>
                          <div className='px-4 sm:px-5 py-2.5 bg-slate-50 border-b border-gray-200'>
                            <div className='text-sm font-semibold text-gray-900'>
                              {formatDateWithSuffix(
                                callFrom === "silProfile"
                                  ? comment?.created_at || comment?.date
                                  : comment?.note_date ||
                                      comment?.created_at ||
                                      comment?.date
                              )}
                            </div>
                          </div>
                          <div className='flex space-x-4 p-4 sm:p-5'>
                          <div className='flex-shrink-0'>
                            <img
                              onError={(e) =>
                                handleImageError(e, comment?.author || " ", " ")
                              }
                              alt='avatar'
                              className='h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover ring-2 ring-gray-200'
                              src={
                                comment.profile_picture
                                  ? baseURLImage(comment.profile_picture)
                                  : getNameProfile(comment?.author || " ", " ")
                              }
                            />
                          </div>
                          <div className='flex-1 min-w-0'>
                            <div className='flex justify-between items-start mb-2'>
                              <div className='flex-1'>
                                <div className='flex items-center gap-2 flex-wrap'>
                                  <span className='font-bold text-sm sm:text-base text-gray-900'>
                                    {comment.author}
                                  </span>
                                  <span
                                    className='text-xs sm:text-sm text-gray-500 bg-gray-100 rounded-full px-2 py-0.5'
                                    title={formatCreatedAtTooltip(comment?.created_at)}
                                  >
                                    {formatRelativeCreatedAt(comment?.created_at)}
                                  </span>
                                </div>
                              </div>
                              <div className='flex items-center space-x-1'>
                                <button
                                  className='p-1 rounded hover:bg-blue-50 transition'
                                  onClick={() => {
                                    setIsOpen(true);
                                    setSelectedComment(comment);
                                  }}
                                  title='Edit'
                                >
                                  <HiOutlinePencil className='text-blue-600 w-4 h-4' />
                                </button>
                                {roleAccess([
                                  USER_ROLE.Sp_Admin,
                                  USER_ROLE.Sp_manager,
                                ]) && (
                                  <button
                                    className='p-1 rounded hover:bg-red-50 transition'
                                    onClick={() => {
                                      if (isUserGracePeriodOver) return;
                                      setSelectedComment(comment);
                                      setDeleteModalOpen(true);
                                    }}
                                    title='Delete'
                                  >
                                    <HiOutlineTrash
                                      className={classNames(
                                        "text-red-500 w-4 h-4",
                                        isUserGracePeriodOver && "opacity-50"
                                      )}
                                    />
                                  </button>
                                )}
                              </div>
                            </div>
                            {callFrom !== "silProfile" && (
                              <div className='mt-3 mb-2 space-y-2 min-w-0'>
                                {comment.title && (
                                  <div className='min-w-0 w-full'>
                                    <span
                                      className='inline-flex max-w-full items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 overflow-hidden text-ellipsis whitespace-nowrap'
                                      title={comment.title}
                                    >
                                      Subject: {comment.title}
                                    </span>
                                  </div>
                                )}
                                <div className='flex flex-nowrap items-center gap-2 overflow-x-auto pb-0.5 min-w-0 custom-scroll'>
                                  <span
                                    className={`inline-flex flex-shrink-0 items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${
                                      comment.is_billable === true
                                        ? "bg-green-50 text-green-700 border-green-200"
                                        : "bg-red-50 text-red-700 border-red-200"
                                    }`}
                                  >
                                    Billable:{" "}
                                    {comment.is_billable === true ? "Yes" : "No"}
                                  </span>
                                  {comment.duration && (
                                    <span className='inline-flex flex-shrink-0 items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-50 text-gray-700 border border-gray-200 whitespace-nowrap'>
                                      Time: {(() => {
                                        const [h, m] =
                                          comment.duration.split(":");
                                        return `${parseInt(h, 10)}h ${parseInt(
                                          m,
                                          10
                                        )}m`;
                                      })()}
                                    </span>
                                  )}
                                  {comment.contact_method && (
                                    <span
                                      className='inline-flex flex-shrink-0 items-center max-w-[200px] px-2.5 py-1 rounded-md text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 overflow-hidden text-ellipsis whitespace-nowrap'
                                      title={comment.contact_method}
                                    >
                                      Contacted: {comment.contact_method}
                                    </span>
                                  )}
                                  {comment.distance_km && (
                                    <span className='inline-flex flex-shrink-0 items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 whitespace-nowrap'>
                                      Travel: {comment.distance_km} KM
                                    </span>
                                  )}
                                  {comment.non_direct_support && (
                                    <span className='inline-flex flex-shrink-0 items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap'>
                                      Non-direct:{" "}
                                      {comment.non_direct_support
                                        ? "Yes"
                                        : "No"}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            {callFrom !== "silProfile" && (
                              <div className='w-full h-px my-3 bg-gray-200' />
                            )}
                            <div className='text-sm text-gray-700 bg-gray-50 rounded-lg p-3 border border-gray-100'>
                              {(comment?.updated_at ||
                                comment?.created_at ||
                                comment?.date) && (
                                <div className='text-xs text-gray-500 mb-2 pb-2 border-b border-gray-200'>
                                  Last updated:{" "}
                                  {formatDateTimeEnAU(
                                    comment.updated_at ||
                                      comment.created_at ||
                                      comment.date
                                  )}
                                </div>
                              )}
                              <div
                                style={{ wordBreak: "break-word" }}
                                className='prose prose-sm max-w-none'
                                dangerouslySetInnerHTML={{
                                  __html: comment.notes,
                                }}
                              />
                            </div>
                            {comment.attachments &&
                              Array.isArray(comment.attachments) &&
                              comment.attachments.length > 0 && (
                                <div className='py-1 w-full min-w-0 mt-2'>
                                  {renderItem(
                                    comment.attachments,
                                    comment?.uuid
                                  )}
                                </div>
                              )}
                          </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  {reachedLast &&
                    paginationObj.totalPages >= paginationObj.page && (
                      <div className='flex justify-center items-center'>
                        <LoaderButton
                          classes='flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-dark hover:bg-blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark'
                          svgClasses='inline mr-3 w-5 h-5 text-white animate-spin'
                          textbutton='Loading More...'
                        />
                      </div>
                    )}
                  {fetchNotes &&
                    fetchNotes.length === 0 &&
                    request.isSuccess && (
                      <div className='bg-white'>
                        <div className='w-full text-center py-12 px-4 sm:px-6 lg:py-16 lg:px-8'>
                          <h2 className='block pt-6 text-xl font-semibold text-gray-900'>
                            No Data Found!
                          </h2>
                          <div className='p-6 flex justify-center'>
                            <div className='mt-2 text-sm text-gray-700 inline-flex'>
                              {`Get started by creating a new ${
                                callFrom === "silProfile" ? "Note" : "case Note"
                              }`}
                            </div>
                          </div>
                          {(roleAccess(
                            [USER_ROLE.Sp_Standard_User],
                            PERMISSION_KEYS.PARTICIPANT,
                            BLK_PORTAL_PREMISSION_KEYS_CHILDREN.PARTICIPANT
                              .CASE_NOTES.STORE
                          ) ||
                            roleAccess([
                              USER_ROLE.Sp_Admin,
                              USER_ROLE.Sp_manager,
                            ])) && (
                            <div className='mt-6 flex justify-center'>
                              <div className='inline-flex'>
                                <CustomButton
                                  label={
                                    callFrom === "silProfile"
                                      ? "Notes"
                                      : "Case Notes"
                                  }
                                  clickHandler={handleFocusOnCaseNote}
                                  Icon={<BiPlus />}
                                  cssClass='bg-gradient-to-br from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white shadow-md hover:shadow-lg'
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                </ul>
              )}
            </div>
          </div>
        </div>
      </section>
      <DeleteCaseNoteModal
        deleteModalOpen={deleteModalOpen}
        selectedComment={selectedComment}
        onCloseModal={() => {
          setSelectedComment(null);
          setDeleteModalOpen(false);
        }}
        fetchNotes={fetchNotes}
        setFetchNotes={setFetchNotes}
        callFrom={callFrom}
      />

      {isOpen && (
        <EditCaseNoteModal
          participantIdUUID={participantInfo?.uuid}
          participantId={participantInfo.id}
          fetchNotes={fetchNotes}
          contactTypes={contactMethods}
          callFrom={callFrom}
          sortCaseNotesByDisplayedDateDesc={sortCaseNotesByDisplayedDateDesc}
          setFetchNotes={setFetchNotes}
          selectedComment={selectedComment}
          isOpen={isOpen}
          onCloseModal={() => {
            setSelectedComment(null);
            setIsOpen(false);
          }}
        />
      )}
    </>
  );
};

export default CaseNotes;
